import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Box,
  Typography,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchLabels,
  addLabel,
  editLabel,
  deleteLabel,
  fetchContactsWithLabel,
  clearErrors,
} from "../../../redux/Slice/Contacts/labelsSlice";
import { isSubscriptionBlockedError } from "../../../utils/subscriptionError";

const LabelsManager = ({ open, onClose, onLabelDeleted }) => {
  const dispatch = useDispatch();
  const { labels, isLoading, isError, errorMessage } = useSelector(
    (state) => state.labels
  );

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [editingLabel, setEditingLabel] = useState(null);
  const [editLabelName, setEditLabelName] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [labelError, setLabelError] = useState("");
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    labelName: "",
    contacts: [],
    loading: false,
  });
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      dispatch(fetchLabels());
    }
  }, [dispatch, open]);

  useEffect(() => {
    if (isError && errorMessage) {
      if (!isSubscriptionBlockedError(errorMessage)) {
        setSnackbar({
          open: true,
          message: errorMessage.error || errorMessage,
          severity: "error",
        });
      }
      dispatch(clearErrors());
    }
  }, [isError, errorMessage, dispatch]);

  const handleClose = () => {
    setEditingLabel(null);
    setEditLabelName("");
    setNewLabel("");
    setLabelError("");
    setDeleteDialog({
      open: false,
      labelName: "",
      contacts: [],
      loading: false,
    });
    onClose();
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({
      open: false,
      labelName: "",
      contacts: [],
      loading: false,
    });
    setDeleteSubmitting(false);
  };

  const openDeleteDialog = async (labelName) => {
    setDeleteDialog({
      open: true,
      labelName,
      contacts: [],
      loading: true,
    });
    try {
      const data = await dispatch(fetchContactsWithLabel(labelName)).unwrap();
      setDeleteDialog((prev) => ({
        ...prev,
        contacts: data.contacts || [],
        loading: false,
      }));
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.error || error || "Failed to load contacts for this label",
        severity: "error",
      });
      closeDeleteDialog();
    }
  };

  const confirmDeleteLabel = async () => {
    const { labelName, contacts } = deleteDialog;
    if (!labelName) return;
    const count = contacts.length;
    setDeleteSubmitting(true);
    try {
      await dispatch(deleteLabel(labelName)).unwrap();
      setSnackbar({
        open: true,
        message:
          count > 0
            ? `Label '${labelName}' deleted and removed from ${count} contact${count === 1 ? "" : "s"}.`
            : `Label '${labelName}' deleted successfully.`,
        severity: "success",
      });
      closeDeleteDialog();
      if (typeof onLabelDeleted === "function") {
        onLabelDeleted();
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.error || "Failed to delete label",
        severity: "error",
      });
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleNewLabelChange = (e) => {
    const value = e.target.value;
    if (value.length > 20) {
      setLabelError("Label cannot exceed 20 characters");
    } else {
      setLabelError("");
    }
    setNewLabel(value);
  };

  const handleAddLabel = async () => {
    if (!newLabel.trim()) return;
    if (labelError) return;

    try {
      await dispatch(addLabel(newLabel.trim())).unwrap();
      setSnackbar({
        open: true,
        message: `Label '${newLabel}' created successfully`,
        severity: "success",
      });
      setNewLabel("");
      setLabelError("");
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.error || "Failed to create label",
        severity: "error",
      });
    }
  };

  const handleEditLabel = async () => {
    if (!editingLabel || !editLabelName.trim()) return;
    if (editLabelName.trim() === editingLabel) {
      setEditingLabel(null);
      setEditLabelName("");
      return;
    }

    try {
      await dispatch(editLabel({
        oldName: editingLabel,
        newName: editLabelName.trim(),
      })).unwrap();
      setSnackbar({
        open: true,
        message: `Label renamed to '${editLabelName}' successfully`,
        severity: "success",
      });
      setEditingLabel(null);
      setEditLabelName("");
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.error || "Failed to rename label",
        severity: "error",
      });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && newLabel.trim() && !labelError) {
      e.preventDefault();
      handleAddLabel();
    }
  };

  const handleEditKeyDown = (e) => {
    if (e.key === "Enter" && editLabelName.trim()) {
      e.preventDefault();
      handleEditLabel();
    } else if (e.key === "Escape") {
      setEditingLabel(null);
      setEditLabelName("");
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Manage Labels</DialogTitle>
        <DialogContent>
          {/* Add New Label */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Add New Label
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <TextField
                fullWidth
                placeholder="Enter new label name"
                value={newLabel}
                onChange={handleNewLabelChange}
                onKeyDown={handleKeyDown}
                error={!!labelError}
                helperText={labelError}
                disabled={isLoading}
                size="small"
              />
              <Button
                variant="contained"
                onClick={handleAddLabel}
                disabled={isLoading || !newLabel.trim() || !!labelError}
                startIcon={isLoading ? <CircularProgress size={20} /> : <SaveIcon />}
                size="small"
              >
                Add
              </Button>
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Existing Labels */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Existing Labels ({labels.length})
            </Typography>
            {labels.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                No labels created yet
              </Typography>
            ) : (
              <List dense>
                {labels.map((label, index) => (
                  <React.Fragment key={label}>
                    <ListItem
                      sx={{
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: "background.paper",
                      }}
                    >
                      {editingLabel === label ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                          <TextField
                            value={editLabelName}
                            onChange={(e) => setEditLabelName(e.target.value)}
                            onKeyDown={handleEditKeyDown}
                            autoFocus
                            size="small"
                            sx={{ flex: 1 }}
                          />
                          <Tooltip title="Save">
                            <IconButton
                              size="small"
                              onClick={handleEditLabel}
                              color="primary"
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancel">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setEditingLabel(null);
                                setEditLabelName("");
                              }}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <>
                          <ListItemText primary={label} />
                          <ListItemSecondaryAction>
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                              <Tooltip title="Edit label">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setEditingLabel(label);
                                    setEditLabelName(label);
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete label">
                                <IconButton
                                  size="small"
                                  onClick={() => openDeleteDialog(label)}
                                  color="error"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </ListItemSecondaryAction>
                        </>
                      )}
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            disabled={isLoading}
            variant="text"
            color="inherit"
            sx={{
              color: "text.primary",
              textTransform: "none",
              fontWeight: 500,
              "&:hover": {
                bgcolor: "action.hover",
              },
              "&.Mui-disabled": {
                color: "action.disabled",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialog.open}
        onClose={deleteSubmitting ? undefined : closeDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete label</DialogTitle>
        <DialogContent>
          {deleteDialog.loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                You are about to delete{" "}
                <strong>{deleteDialog.labelName}</strong>. It will be removed
                from your label list and from every contact that has it.
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                Contacts with this label ({deleteDialog.contacts.length})
              </Typography>
              {deleteDialog.contacts.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No contacts currently have this label.
                </Typography>
              ) : (
                <List
                  dense
                  sx={{
                    maxHeight: 260,
                    overflow: "auto",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                  }}
                >
                  {deleteDialog.contacts.map((c) => (
                    <ListItem key={c._id}>
                      <ListItemText
                        primary={c.name || "Unnamed"}
                        secondary={c.phoneNumberID || undefined}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeDeleteDialog}
            disabled={deleteDialog.loading || deleteSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteLabel}
            color="error"
            variant="contained"
            disabled={
              deleteDialog.loading || deleteSubmitting || !deleteDialog.labelName
            }
          >
            {deleteSubmitting ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              "Delete label"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%", maxWidth: 480 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default LabelsManager;
